import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import PublicLayout from '../../components/public/PublicLayout';
import { trackEvent } from '../../publicSite/analytics';
import { APPLY_ROUTE, withBase } from '../../publicSite/config';
import { formatEventWindow, splitEventsByTimeline } from '../../publicSite/events';
import { homepageDefaults } from '../../publicSite/homepageDefaults';
import { usePageMeta } from '../../publicSite/usePageMeta';
import { usePublicEvents } from '../../publicSite/usePublicEvents';
import { db } from '../../firebase';

const normalizeHomepageContent = (content) => {
  const usesPreviousVideo = content?.video?.url === 'https://www.youtube.com/watch?v=457OOS_zFjA';
  const usesPreviousVideoHeading = content?.video?.heading === 'Course Overview Video';
  const usesPreviousHero = content?.hero?.title === 'Welcome to the School of Shem';
  if (usesPreviousVideoHeading || usesPreviousVideo || usesPreviousHero) {
    return {
      ...content,
      ...(usesPreviousHero ? { hero: homepageDefaults.hero } : {}),
      ...((usesPreviousVideoHeading || usesPreviousVideo) ? { video: {
        ...(content.video || {}),
        heading: 'Video of the Week',
        ...(usesPreviousVideo ? { url: homepageDefaults.video.url } : {})
      } } : {})
    };
  }
  return content;
};

const toYoutubeEmbed = (url = '') => {
  const match = String(url).match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/)([a-zA-Z0-9_-]{6,})/
  );
  return match ? `https://www.youtube-nocookie.com/embed/${match[1]}` : '';
};

const eventDateParts = (event) => {
  const parsed = new Date(event?.eventStartDate || event?.eventDate || '');
  if (Number.isNaN(parsed.getTime())) return { day: '—', month: 'TBC' };
  return {
    day: parsed.toLocaleDateString('en-ZA', { day: '2-digit' }),
    month: parsed.toLocaleDateString('en-ZA', { month: 'short' }).toUpperCase()
  };
};

const eventSignupPath = (event) => (
  `/events/signup?eventId=${encodeURIComponent(event?.id || '')}` +
  `&title=${encodeURIComponent(event?.title || 'School of Shem Event')}` +
  `&date=${encodeURIComponent(formatEventWindow(event?.eventStartDate || event?.eventDate, event?.eventEndDate))}` +
  `&image=${encodeURIComponent(event?.downloadUrl || event?.image || '')}`
);

export default function Home() {
  usePageMeta(
    'School of Shem | Kingdom Ecosystem for Mastery',
    'School of Shem equips believers with structured, faith-aligned learning across wealth, leadership, family, and purpose.'
  );
  const [homepageContent, setHomepageContent] = useState(homepageDefaults);
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const { events } = usePublicEvents();
  const { upcoming } = splitEventsByTimeline(events);

  useEffect(() => {
    if (!useFirebase) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, 'settings', 'publicSite'),
      (snapshot) => {
        const saved = snapshot.exists() ? snapshot.data()?.homepageContent : null;
        if (!saved) {
          setHomepageContent(homepageDefaults);
          return;
        }
        const normalized = normalizeHomepageContent(saved);
        setHomepageContent({
          ...homepageDefaults,
          ...normalized,
          hero: { ...homepageDefaults.hero, ...(normalized.hero || {}) },
          heroSlides: normalized.heroSlides?.length ? normalized.heroSlides : homepageDefaults.heroSlides,
          courses: {
            ...homepageDefaults.courses,
            ...(normalized.courses || {}),
            cards: normalized.courses?.cards?.length ? normalized.courses.cards : homepageDefaults.courses.cards
          },
          video: { ...homepageDefaults.video, ...(normalized.video || {}) },
          whyChoose: {
            ...homepageDefaults.whyChoose,
            ...(normalized.whyChoose || {}),
            cards: normalized.whyChoose?.cards?.length
              ? normalized.whyChoose.cards
              : homepageDefaults.whyChoose.cards
          },
          events: { ...homepageDefaults.events, ...(normalized.events || {}) },
          cta: { ...homepageDefaults.cta, ...(normalized.cta || {}) }
        });
      },
      () => setHomepageContent(homepageDefaults)
    );
    return () => unsubscribe();
  }, [useFirebase]);

  const heroImage = useMemo(
    () => {
      const selected = homepageContent.heroSlides?.find((slide) => slide?.src)?.src || '';
      return selected && selected !== '/School Of SHEM Welcome.png'
        ? selected
        : '/images/shem-campus-hero.jpg';
    },
    [homepageContent.heroSlides]
  );
  const academicCards = (homepageContent.courses?.cards || []).slice(0, 6);
  const reasons = (homepageContent.whyChoose?.cards || []).slice(0, 4);
  const featuredEvents = upcoming.slice(0, 3);
  const videoUrl = toYoutubeEmbed(homepageContent.video?.url);

  return (
    <PublicLayout>
      <section className="public-home-hero">
        <div className="public-home-hero-inner">
          <div className="public-home-hero-copy">
            <div className="ps-hero-eyebrow">{homepageContent.hero?.eyebrow || 'School of SHEM'}</div>
            <h1>{homepageContent.hero?.title}</h1>
            <p>{homepageContent.hero?.subtitle}</p>
            <div className="ps-hero-actions">
              <Link
                to={withBase(APPLY_ROUTE)}
                className="btn btn-primary"
                onClick={() => trackEvent('cta_click', { label: 'home_apply' })}
              >
                Apply Now
              </Link>
              <Link to={withBase('/academics/faculties')} className="btn btn-ghost-light">
                Explore Academics <span aria-hidden="true">↗</span>
              </Link>
            </div>
            <div className="public-hero-proof" aria-label="School highlights">
              <div><strong>13</strong><span>Specialised faculties</span></div>
              <div><strong>1</strong><span>Whole-life ecosystem</span></div>
              <div><strong>∞</strong><span>Generational impact</span></div>
            </div>
          </div>
          <figure className="public-home-hero-media">
            {heroImage && (
              <img src={heroImage} alt="School of SHEM learning community" />
            )}
            <figcaption>
              <span>Johannesburg, South Africa</span>
              <strong>Build a life that outlives you.</strong>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="ps-section public-manifesto-section">
        <div className="ps-section-inner public-manifesto">
          <div className="public-manifesto-copy">
            <div className="ps-hero-eyebrow">More than a school</div>
            <h2>A Kingdom ecosystem for success, stability, and economic mastery.</h2>
            <p>
              We bring faith, family, leadership, enterprise, and purpose into one connected learning experience—so
              insight becomes structure and structure becomes lasting impact.
            </p>
            <Link className="public-text-link" to={withBase('/about')}>Discover our story <span aria-hidden="true">→</span></Link>
          </div>
          <figure className="public-manifesto-media">
            <img src="/images/shem-academics-masterclass.jpg" alt="Adult learners engaging in a guided masterclass" loading="lazy" />
            <figcaption>
              <span>Formation in community</span>
              <strong>Wisdom shared. Purpose sharpened.</strong>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="ps-section ps-section-muted">
        <div className="ps-section-inner">
          <div className="ps-section-heading-row">
            <div>
              <div className="ps-hero-eyebrow">Academic pathways</div>
              <h2 className="ps-section-title">Explore our academics</h2>
              <p className="ps-section-copy">
                Choose a focused pathway for practical, faith-aligned growth across the domains that shape life.
              </p>
            </div>
            <Link className="public-text-link" to={withBase('/academics/faculties')}>
              View all faculties <span aria-hidden="true">→</span>
            </Link>
          </div>
          {academicCards.length > 0 ? (
            <div className="public-editorial-grid">
              {academicCards.map((card, index) => (
                <article className="public-editorial-card" key={card.title}>
                  <span className="public-card-number">{String(index + 1).padStart(2, '0')}</span>
                  {card.image && (
                    <div className="public-editorial-card-media">
                      <img src={card.image} alt="" loading="lazy" />
                    </div>
                  )}
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <Link className="public-text-link" to={withBase(card.link || '/academics/faculties')}>
                    Explore <span aria-hidden="true">→</span>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <p className="ps-empty-state">Academic pathways will be published here soon.</p>
          )}
        </div>
      </section>

      <section className="ps-section public-formation-section">
        <div className="ps-section-inner public-formation-layout">
          <figure className="public-formation-media">
            <img src="/images/shem-mentorship.jpg" alt="A mentor and learner developing a practical plan" loading="lazy" />
            <figcaption>Personal guidance. Practical implementation.</figcaption>
          </figure>
          <div>
            <div className="ps-hero-eyebrow">The School of SHEM difference</div>
            <h2 className="ps-section-title">{homepageContent.whyChoose?.heading || 'Why School of SHEM'}</h2>
            <div className="public-reason-list">
              {reasons.map((reason, index) => (
                <article key={reason.title}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <div>
                    <h3>{reason.title}</h3>
                    <p>{reason.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {videoUrl && (
        <section className="ps-section ps-section-muted">
          <div className="ps-section-inner public-video-layout">
            <div>
              <div className="ps-hero-eyebrow">Watch</div>
              <h2 className="ps-section-title">{homepageContent.video?.heading}</h2>
              <p className="ps-section-copy">Teaching and insight from the School of SHEM community.</p>
            </div>
            <div className="public-video-frame">
              <iframe
                title={homepageContent.video?.heading || 'School of SHEM video'}
                src={videoUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      )}

      <section className="ps-section">
        <div className="ps-section-inner">
          <div className="ps-section-heading-row">
            <div>
              <div className="ps-hero-eyebrow">Gather and grow</div>
              <h2 className="ps-section-title">{homepageContent.events?.heading || 'Upcoming events'}</h2>
            </div>
            <Link className="public-text-link" to={withBase('/events')}>
              View all events <span aria-hidden="true">→</span>
            </Link>
          </div>
          {featuredEvents.length > 0 ? (
            <div className="public-event-list">
              {featuredEvents.map((event) => {
                const date = eventDateParts(event);
                return (
                  <article className="public-event-row" key={event.id}>
                    <time className="public-event-date" dateTime={event.eventStartDate || event.eventDate || ''}>
                      <strong>{date.day}</strong>
                      <span>{date.month}</span>
                    </time>
                    <div>
                      <h3>{event.title}</h3>
                      <p>{event.description || event.notes || 'School of SHEM event'}</p>
                    </div>
                    <span>{event.location || event.eventLocation || event.module || 'Details on registration'}</span>
                    <Link className="public-text-link" to={withBase(eventSignupPath(event))}>
                      View event <span aria-hidden="true">→</span>
                    </Link>
                  </article>
                );
              })}
            </div>
          ) : (
            <p className="ps-empty-state">No upcoming events have been posted yet.</p>
          )}
        </div>
      </section>

      <section className="ps-section public-cta">
        <div className="ps-section-inner public-cta-inner">
          <div>
            <h2>{homepageContent.cta?.title || 'Ready to take the next step?'}</h2>
            <p>{homepageContent.cta?.subtitle}</p>
          </div>
          <div className="ps-hero-actions">
            <Link
              to={withBase(APPLY_ROUTE)}
              className="btn btn-primary"
              onClick={() => trackEvent('cta_click', { label: 'home_apply_footer' })}
            >
              Apply Now
            </Link>
            <Link to={withBase('/contact')} className="btn btn-outline">Contact Us</Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
